import { useEffect, useRef, useState } from 'react';
import type { UserProfile } from '../shared/types.ts';
import { OjisanBubble, UserBubble } from './components/Bubble.tsx';
import { Composer } from './components/Composer.tsx';
import { Intake } from './components/Intake.tsx';
import { Lamp } from './components/Lamp.tsx';
import { Sources } from './components/Sources.tsx';
import { Suggestions } from './components/Suggestions.tsx';
import { useChat } from './hooks/useChat.ts';

const PROFILE_KEY = 'kigyou-ojisan.profile';

function loadProfile(): UserProfile | null {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? (JSON.parse(raw) as UserProfile) : null;
  } catch {
    return null;
  }
}

const GREETING = `どうも、起業おじさんです。

起業の話でも、その前の「やりたいかどうか分からない」でも、うまくいっていない話でも、何でも聞きます。まとまっていなくて大丈夫なので、いま頭にあることをそのまま書いてください。`;

export default function App() {
  const [profile, setProfile] = useState<UserProfile | null>(loadProfile);
  const [intakeDone, setIntakeDone] = useState<boolean>(() => loadProfile() !== null);
  const { turns, thinking, streaming, error, send, reset } = useChat(profile);
  const threadRef = useRef<HTMLDivElement>(null);

  // 新しい発言が増えたら最新に追従する
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, thinking]);

  const saveProfile = (next: UserProfile) => {
    setProfile(next);
    setIntakeDone(true);
    try {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(next));
    } catch {
      // プライベートモード等で保存できなくても会話は続けられる
    }
  };

  const startOver = () => {
    reset();
    setIntakeDone(profile !== null);
  };

  return (
    <div className="app">
      <header className="masthead">
        <Lamp thinking={thinking} />
        <div>
          <h1 className="masthead__title">起業おじさん</h1>
          <p className="masthead__tagline">話を聞いて、次の一歩を一緒に決めます</p>
        </div>
        <div className="masthead__spacer" />
        {turns.length > 0 && (
          <button type="button" className="ghost-button" onClick={startOver}>
            最初から
          </button>
        )}
      </header>

      <main className="thread" ref={threadRef}>
        <div className="thread__inner">
          <div className="turn">
            <span className="turn__speaker">ojisan</span>
            <OjisanBubble text={GREETING} streaming={false} />
          </div>

          {!intakeDone && (
            <Intake onSubmit={saveProfile} onSkip={() => setIntakeDone(true)} />
          )}

          {turns.map((turn) =>
            turn.role === 'user' ? (
              <div className="turn turn--user" key={turn.id}>
                <span className="turn__speaker">you</span>
                <UserBubble text={turn.text} />
              </div>
            ) : (
              <div className="turn" key={turn.id}>
                <span className="turn__speaker">ojisan</span>
                <OjisanBubble text={turn.text} streaming={streaming && turn.text.length > 0} />
                {turn.sources && !streaming && <Sources sources={turn.sources} />}
                <Suggestions offers={turn.offers} resources={turn.resources} />
              </div>
            ),
          )}

          {thinking && (
            <div className="turn">
              <p className="thinking">
                <Lamp thinking />
                考えています
              </p>
            </div>
          )}

          {error && (
            <p className="notice" role="alert">
              {error}
            </p>
          )}
        </div>
      </main>

      <Composer onSend={send} disabled={streaming} showStarters={turns.length === 0 && intakeDone} />
    </div>
  );
}
